import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Pencil, Plus, Loader2, FolderTree, Trash2, History } from "lucide-react";


export default function Products() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [edit, setEdit] = useState(null);
  const [catModal, setCatModal] = useState(false);
  const [history, setHistory] = useState(null);
  const [filterCat, setFilterCat] = useState("all");

  const load = () => {
    api.get("/admin/products").then((r) => setProducts(r.data));
    api.get("/admin/categories").then((r) => setCategories(r.data));
    api.get("/admin/policy").then((r) => setPolicy(r.data));
  };
  useEffect(() => { load(); }, []);

  const newProduct = () =>
    setEdit({ name: "", pack: "", category: categories[0]?.name || "", subcategory: "", list_price: "", cost: "", vat_rate: 0.05, active: true });

  const grouped = useMemo(() => {
    if (!products) return {};
    const out = {};
    products.filter((p) => filterCat === "all" || p.category === filterCat).forEach((p) => {
      const k = p.category || "Uncategorised";
      (out[k] = out[k] || []).push(p);
    });
    return out;
  }, [products, filterCat]);

  const showHistory = () => api.get("/admin/products/price-history").then((r) => setHistory(r.data));

  return (
    <div>
      <PageHeader
        title="Products & Pricing"
        subtitle={`Edit cost (incl. VAT) and full list price - the 0-5% discount grid and per-bag commission follow the policy formula (${policy ? Math.round(policy.policy_pct * 100) : "…"}% of gross profit ex-VAT) automatically. Cost is internal - never shown to agents.`}
        actions={
          <>
            <Button variant="outline" onClick={showHistory} data-testid="price-history-button"><History className="w-4 h-4 mr-1" /> Price history</Button>
            {isAdmin && <Button variant="outline" onClick={() => setCatModal(true)} data-testid="manage-categories-button"><FolderTree className="w-4 h-4 mr-1" /> Categories</Button>}
            {isAdmin && <Button onClick={newProduct} data-testid="add-product-button"><Plus className="w-4 h-4 mr-1" /> Add product</Button>}
          </>
        }
      />

      <div className="flex items-center gap-2 mb-4">
        <Label className="text-xs text-muted-foreground">Category</Label>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-48 h-9" data-testid="category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!products ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : products.length === 0 ? (
        <Empty>No products yet. Add your first product.</Empty>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([cat, list]) => (
            <Card key={cat} className="overflow-hidden" data-testid={`category-group-${cat}`}>
              <div className="px-4 py-2.5 border-b border-border bg-secondary/40 flex items-center gap-2">
                <FolderTree className="w-4 h-4 text-primary" />
                <span className="font-semibold text-sm">{cat}</span>
                <span className="text-xs text-muted-foreground">· {list.length} product{list.length !== 1 && "s"}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="text-left font-medium px-4 py-3">Product</th>
                      <th className="text-left font-medium px-4 py-3">Sub-category</th>
                      <th className="text-right font-medium px-4 py-3">Cost (incl VAT)</th>
                      <th className="text-right font-medium px-4 py-3">List price</th>
                      <th className="text-right font-medium px-4 py-3">GP ex-VAT</th>
                      <th className="text-right font-medium px-4 py-3">Comm/bag</th>
                      <th className="text-right font-medium px-4 py-3">Comm @5%</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((p) => (
                      <tr key={p.id} className={`border-b border-border/50 hover:bg-secondary/40 ${!p.active && "opacity-50"}`} data-testid={`product-row-${p.id}`}>
                        <td className="px-4 py-3 font-medium">{p.name} <span className="text-muted-foreground">· {p.pack}</span>{!p.active && <Badge variant="outline" className="ml-2">inactive</Badge>}</td>
                        <td className="px-4 py-3 text-muted-foreground">{p.subcategory || "—"}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">{p.cost} AED</td>
                        <td className="px-4 py-3 text-right">{p.list_price} AED</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">{p.gross_profit_ex_vat ?? "—"}</td>
                        <td className="px-4 py-3 text-right text-primary font-semibold">{p.commission_per_bag} AED</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">{p.discount_commissions?.[5]} AED</td>
                        <td className="px-4 py-3 text-right">
                          {isAdmin && <Button variant="ghost" size="sm" onClick={() => setEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>
      )}

      {edit && <ProductDialog edit={edit} setEdit={setEdit} categories={categories} policy={policy} onSaved={load} />}
      <CategoriesDialog open={catModal} onClose={() => setCatModal(false)} categories={categories} onChanged={load} />

      <Dialog open={!!history} onOpenChange={(v) => !v && setHistory(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Price & cost change history</DialogTitle></DialogHeader>
          {history && history.length === 0 ? <Empty>No changes recorded yet.</Empty> : (
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase text-muted-foreground border-b"><th className="text-left py-2">When</th><th className="text-left py-2">Product</th><th className="text-right py-2">Price</th><th className="text-right py-2">Cost</th><th className="text-right py-2">Comm</th><th className="text-left py-2 pl-3">By</th></tr></thead>
              <tbody>{(history || []).map((h) => (
                <tr key={h.id} className="border-b border-border/40">
                  <td className="py-2 text-muted-foreground">{h.at?.slice(0, 10)}</td>
                  <td className="py-2">{h.product || h.product_id}</td>
                  <td className="py-2 text-right">{h.old_price} → <b>{h.new_price}</b></td>
                  <td className="py-2 text-right">{h.old_cost ?? "—"} → <b>{h.new_cost ?? "—"}</b></td>
                  <td className="py-2 text-right">{h.old_commission ?? "—"} → <b>{h.new_commission ?? "—"}</b></td>
                  <td className="py-2 pl-3 text-xs text-muted-foreground">{h.changed_by}</td>
                </tr>))}</tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProductDialog({ edit, setEdit, categories, policy, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [saving, setSaving] = useState(false);
  const [grid, setGrid] = useState(null);
  const cat = categories.find((c) => c.name === edit.category);
  const cost = Number(edit.cost), sell = Number(edit.list_price), vat = Number(edit.vat_rate);

  useEffect(() => {
    if (!(sell > 0) || !(cost >= 0) || cost >= sell) { setGrid(null); return; }
    const t = setTimeout(() => {
      api.post("/admin/products/preview-grid", { cost, list_price: sell, vat_rate: vat }).then((r) => setGrid(r.data)).catch(() => setGrid(null));
    }, 250);
    return () => clearTimeout(t);
  }, [cost, sell, vat]);

  const save = async () => {
    if (!edit.name || !edit.pack) return toast.error("Name and pack are required");
    if (!(sell > 0) || cost >= sell) return toast.error("List price must be above cost");
    setSaving(true);
    try {
      const payload = { name: edit.name, pack: edit.pack, category: edit.category || "", subcategory: edit.subcategory || "", list_price: sell, cost, vat_rate: vat, active: edit.active !== false,
        effective_from: edit.effective_from || null, note: edit.version_note || "" };
      let res;
      if (edit.id) res = await api.put(`/admin/products/${edit.id}`, payload);
      else res = await api.post("/admin/products", payload);
      toast.success(res?.data?.scheduled_version ? `Price change scheduled for ${res.data.scheduled_version.effective_from} - current prices stay live until then` : edit.id ? "Product updated - commission grid recalculated" : "Product added");
      setEdit(null);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && setEdit(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{edit.id ? `${edit.name} · ${edit.pack}` : "New product"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label="Product name"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="product-name-input" /></F>
          <F label="Pack / size"><Input value={edit.pack} placeholder="e.g. 40 kg" onChange={(e) => setEdit({ ...edit, pack: e.target.value })} data-testid="product-pack-input" /></F>
          <F label="Category">
            <Select value={edit.category || ""} onValueChange={(v) => setEdit({ ...edit, category: v, subcategory: "" })}>
              <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Sub-category">
            <Select value={edit.subcategory || ""} onValueChange={(v) => setEdit({ ...edit, subcategory: v })} disabled={!cat?.subcategories?.length}>
              <SelectTrigger data-testid="product-subcategory-select"><SelectValue placeholder={cat?.subcategories?.length ? "Select" : "None"} /></SelectTrigger>
              <SelectContent>{(cat?.subcategories || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-1">
          <F label="Cost incl. VAT (AED)"><Input type="number" step="0.01" value={edit.cost} onChange={(e) => setEdit({ ...edit, cost: e.target.value })} data-testid="product-cost-input" className="border-amber-400/70 bg-amber-50/40 dark:bg-amber-950/20" /></F>
          <F label="Full list price (AED)"><Input type="number" step="0.01" value={edit.list_price} onChange={(e) => setEdit({ ...edit, list_price: e.target.value })} data-testid="product-list-price" className="border-amber-400/70 bg-amber-50/40 dark:bg-amber-950/20" /></F>
          <F label="VAT rate"><Input type="number" step="0.01" value={edit.vat_rate} onChange={(e) => setEdit({ ...edit, vat_rate: e.target.value })} /></F>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <F label="Price effective from (blank = today; future date schedules it)"><Input type="date" value={edit.effective_from || ""} onChange={(e) => setEdit({ ...edit, effective_from: e.target.value })} data-testid="product-effective-from-input" /></F>
          <F label="Version note"><Input value={edit.version_note || ""} placeholder="e.g. Q4 supplier increase" onChange={(e) => setEdit({ ...edit, version_note: e.target.value })} data-testid="product-version-note-input" /></F>
        </div>
        {edit.id && <VersionHistory productId={edit.id} isAdmin={isAdmin} />}

        <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-3" data-testid="grid-preview">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Auto-calculated grid · policy {policy ? Math.round(policy.policy_pct * 100) : "…"}% × gross profit ex-VAT</p>
            {grid && <span className="text-xs text-muted-foreground">GP ex-VAT {grid.gross_profit_ex_vat} · owner keeps ~{policy ? Math.round((1 - policy.policy_pct) * 100) : "…"}%</span>}
          </div>
          {!grid ? (
            <p className="text-xs text-muted-foreground">Enter a cost and a list price above cost to preview.</p>
          ) : (
            <table className="w-full text-xs">
              <thead><tr className="text-muted-foreground"><th className="text-left py-1">Discount</th>{grid.discount_prices.map((_, i) => <th key={`h${i}`} className={`text-right py-1 ${i > 5 ? "text-amber-600" : ""}`}>{i}%{i > 5 ? "†" : ""}</th>)}</tr></thead>
              <tbody>
                <tr><td className="py-1 text-muted-foreground">Sale price</td>{grid.discount_prices.map((v, i) => <td key={`p${i}`} className="text-right py-1">{v}</td>)}</tr>
                <tr><td className="py-1 text-muted-foreground">Commission</td>{grid.discount_commissions.map((v, i) => <td key={`c${i}`} className="text-right py-1 font-semibold text-primary" data-testid={`grid-comm-${i}`}>{v}</td>)}</tr>
              </tbody>
            </table>
          )}
          {grid && <p className="text-[10px] text-muted-foreground mt-1">† above 5% requires manager approval (may carry no commission)</p>}
        </div>

        <DialogFooter className="mt-2 flex items-center justify-between sm:justify-between">
          {edit.id ? (
            <Button variant="ghost" className="text-muted-foreground" onClick={() => setEdit({ ...edit, active: !(edit.active !== false) })}>
              {edit.active !== false ? "Mark inactive" : "Mark active"}
            </Button>
          ) : <span />}
          <Button onClick={save} disabled={saving} data-testid="save-product-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : edit.id ? "Save & recalculate" : "Add product"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoriesDialog({ open, onClose, categories, onChanged }) {
  const [draft, setDraft] = useState({ name: "", subs: "" });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    const body = { name: draft.name.trim(), subcategories: draft.subs.split(",").map((s) => s.trim()).filter(Boolean) };
    if (!body.name) return toast.error("Category name required");
    try {
      if (editing) await api.put(`/admin/categories/${editing}`, body);
      else await api.post("/admin/categories", body);
      toast.success(editing ? "Category updated" : "Category added");
      setDraft({ name: "", subs: "" }); setEditing(null); onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const remove = async (c) => {
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Category removed"); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Categories & sub-categories</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {categories.map((c) => (
            <div key={c.id} className="flex items-start justify-between rounded-lg border border-border p-3" data-testid={`category-item-${c.id}`}>
              <div>
                <p className="font-medium text-sm">{c.name}</p>
                <div className="flex flex-wrap gap-1 mt-1">{(c.subcategories || []).map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}{!c.subcategories?.length && <span className="text-xs text-muted-foreground">no sub-categories</span>}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(c.id); setDraft({ name: c.name, subs: (c.subcategories || []).join(", ") }); }}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(c)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-lg bg-secondary/40 p-3 space-y-2 mt-2">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{editing ? "Edit category" : "Add category"}</p>
          <Input placeholder="Category name, e.g. Grains" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="category-name-input" />
          <Input placeholder="Sub-categories, comma separated, e.g. Basmati, Non-Basmati" value={draft.subs} onChange={(e) => setDraft({ ...draft, subs: e.target.value })} data-testid="category-subs-input" />
          <div className="flex justify-end gap-2">
            {editing && <Button variant="ghost" size="sm" onClick={() => { setEditing(null); setDraft({ name: "", subs: "" }); }}>Cancel</Button>}
            <Button size="sm" onClick={save} data-testid="save-category-button">{editing ? "Save" : "Add"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}

function VersionHistory({ productId, isAdmin }) {
  const [vers, setVers] = useState(null);
  const load = useCallback(() => api.get(`/admin/products/${productId}/versions`).then((r) => setVers(r.data)), [productId]);
  useEffect(() => { load(); }, [load]);
  const cancel = async (v) => {
    try { await api.delete(`/admin/products/${productId}/versions/${v.id}`); toast.success("Scheduled change cancelled"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!vers) return null;
  return (
    <div className="mt-3 rounded-lg border border-border/60 p-3" data-testid="product-version-history">
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Price versions</p>
      <div className="space-y-1 text-xs">
        {vers.map((v) => (
          <div key={v.id} className="flex items-center justify-between gap-2" data-testid={`version-row-${v.id}`}>
            <span><Badge variant={v.status === "active" ? "default" : v.status === "scheduled" ? "secondary" : "outline"} className="mr-2 capitalize">{v.status}</Badge>from {v.effective_from}{v.effective_to ? ` to ${v.effective_to}` : ""} · cost {v.cost} · list {v.list_price} · comm {v.commission_per_bag ?? "—"}{v.note ? ` · ${v.note}` : ""} <span className="text-muted-foreground">({v.set_by})</span></span>
            {v.status === "scheduled" && isAdmin && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => cancel(v)} data-testid={`cancel-version-${v.id}`}>Cancel</Button>}
          </div>
        ))}
      </div>
    </div>
  );
}
